import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPostBySlug, blogPosts } from '../data/blogPosts';
import SEO from '../components/SEO';
import SchemaOrg from '../components/SchemaOrg';

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getBlogPostBySlug(slug) : undefined;

  if (!post) {
    return (
      <div className="pt-32 pb-24 bg-[#050505]">
        <SEO
          title="Artículo no encontrado"
          description="El artículo que buscas no existe o fue movido."
          url="https://lqpglobal.com/blog"
        />
        <div className="container mx-auto px-6 text-center">
          <h1 className="text-4xl font-bold text-white mb-4">Artículo no encontrado</h1>
          <p className="text-gray-400 mb-8">El artículo que buscas no existe o fue movido.</p>
          <Link
            to="/blog"
            className="px-6 py-3 bg-red-600 text-white rounded-xl font-bold hover:bg-red-700 transition-all"
          >
            Volver al Blog
          </Link>
        </div>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.id !== post.id && p.category === post.category)
    .slice(0, 2);

  return (
    <article className="pt-32 pb-24 bg-[#050505]">
      <SEO
        title={post.title}
        description={post.metaDescription}
        keywords={post.keywords}
        image={post.image}
        url={`https://lqpglobal.com/blog/${post.slug}`}
        type="article"
        publishedTime={post.dateISO}
        section={post.category}
      />
      <SchemaOrg
        type="article"
        title={post.title}
        description={post.metaDescription}
        image={post.image}
        url={`https://lqpglobal.com/blog/${post.slug}`}
        datePublished={post.dateISO}
        section={post.category}
      />

      <div className="container mx-auto px-6">
        {/* Breadcrumbs */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-2 text-sm text-gray-500" itemScope itemType="https://schema.org/BreadcrumbList">
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/" className="hover:text-white transition-colors" itemProp="item">
                <span itemProp="name">Inicio</span>
              </Link>
              <meta itemProp="position" content="1" />
            </li>
            <span>/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <Link to="/blog" className="hover:text-white transition-colors" itemProp="item">
                <span itemProp="name">Blog</span>
              </Link>
              <meta itemProp="position" content="2" />
            </li>
            <span>/</span>
            <li itemProp="itemListElement" itemScope itemType="https://schema.org/ListItem">
              <span className="text-gray-400" itemProp="name">{post.category}</span>
              <meta itemProp="position" content="3" />
            </li>
          </ol>
        </nav>

        <header className="max-w-4xl mb-12">
          <div className="inline-block px-4 py-1 rounded-full bg-red-500/10 border border-red-500/20 text-xs font-bold text-red-500 mb-6 uppercase tracking-widest">
            {post.category}
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
            {post.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-8">
            <time dateTime={post.dateISO}>{post.date}</time>
            <span className="w-1 h-1 rounded-full bg-gray-700"></span>
            <span>{post.readTime} de lectura</span>
          </div>
          <p className="text-xl text-gray-400 leading-relaxed">
            {post.excerpt}
          </p>
        </header>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <figure className="mb-12">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-[400px] object-cover rounded-3xl"
                loading="eager"
              />
            </figure>

            <div
              className="prose prose-invert prose-lg max-w-none
                prose-headings:text-white prose-headings:font-bold
                prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-4
                prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-6
                prose-li:text-gray-300
                prose-strong:text-white
                prose-ul:my-6 prose-ul:space-y-2
                prose-a:text-red-500 prose-a:no-underline hover:prose-a:underline"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* CTA */}
            <div className="mt-16 p-8 md:p-12 glass rounded-3xl border-white/10">
              <h3 className="text-2xl font-bold text-white mb-4">
                ¿Querés implementar estas ideas en tu negocio?
              </h3>
              <p className="text-gray-400 mb-6">
                Agendá una consultoría gratuita y analicemos juntos cómo mejorar tu operación.
              </p>
              <Link
                to="/contacto"
                className="inline-block px-8 py-4 bg-red-600 text-white font-bold rounded-xl hover:bg-red-700 transition-all"
              >
                Agendar Consultoría Gratuita
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-32 space-y-8">
              {/* Author */}
              <div className="glass p-6 rounded-2xl border-white/10">
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src="https://lqpglobal.com/logocubo.png"
                    alt="LQP Global"
                    className="w-12 h-12 object-contain"
                  />
                  <div>
                    <div className="font-bold text-white">LQP Global</div>
                    <div className="text-sm text-gray-500">Equipo de contenidos</div>
                  </div>
                </div>
                <p className="text-sm text-gray-400">
                  Compartimos conocimiento sobre digitalización, automatización e IA para pymes que quieren crecer con orden.
                </p>
              </div>

              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="glass p-6 rounded-2xl border-white/10">
                  <h3 className="font-bold text-white mb-4">Artículos relacionados</h3>
                  <div className="space-y-4">
                    {relatedPosts.map(related => (
                      <Link
                        key={related.id}
                        to={`/blog/${related.slug}`}
                        className="block group"
                      >
                        <div className="text-sm text-gray-400 group-hover:text-white transition-colors">
                          {related.title}
                        </div>
                        <div className="text-xs text-gray-600 mt-1">{related.readTime}</div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Categories */}
              <div className="glass p-6 rounded-2xl border-white/10">
                <h3 className="font-bold text-white mb-4">Categorías</h3>
                <div className="flex flex-wrap gap-2">
                  {Array.from(new Set(blogPosts.map(p => p.category))).map(cat => (
                    <Link
                      key={cat}
                      to="/blog"
                      className="px-3 py-1 text-xs bg-white/5 border border-white/10 rounded-full text-gray-400 hover:text-white hover:border-white/30 transition-all"
                    >
                      {cat}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* Back to Blog */}
        <div className="mt-16 pt-8 border-t border-white/10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors"
          >
            <span>←</span>
            <span>Volver al Blog</span>
          </Link>
        </div>
      </div>
    </article>
  );
};

export default BlogPostPage;
